import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../api/ApiProvider.js';
import { useActiveClassroom } from '../../context/ActiveClassroomProvider.js';
import { useFitScale } from '../../hooks/useFitScale.js';
import {
  formatTemperature,
  getTemperatureUnit,
  type TemperatureUnit,
} from '../../classroomPages/temperatureUnitStorage.js';
import { Thermometer } from './Thermometer.js';
import { WeatherHistoryModal } from './WeatherHistoryModal.js';
import sunnyImg from '../../../../../assets/weather/sunny.png';
import partlySunnyImg from '../../../../../assets/weather/partly-sunny.png';
import cloudyImg from '../../../../../assets/weather/cloudy.png';
import rainyImg from '../../../../../assets/weather/rainy.png';
import thunderImg from '../../../../../assets/weather/thunder.png';
import snowyImg from '../../../../../assets/weather/snowy.png';
import windyImg from '../../../../../assets/weather/windy.png';
import foggyImg from '../../../../../assets/weather/foggy.png';
import styles from './Weather.module.css';

const KINDS: { kind: string; label: string; img: string }[] = [
  { kind: 'sunny', label: 'Sunny', img: sunnyImg },
  { kind: 'partly-sunny', label: 'Partly Sunny', img: partlySunnyImg },
  { kind: 'cloudy', label: 'Cloudy', img: cloudyImg },
  { kind: 'rainy', label: 'Rainy', img: rainyImg },
  { kind: 'thunder', label: 'Thunder', img: thunderImg },
  { kind: 'snowy', label: 'Snowy', img: snowyImg },
  { kind: 'windy', label: 'Windy', img: windyImg },
  { kind: 'foggy', label: 'Foggy', img: foggyImg },
];

/** Local-clock YYYY-MM-DD — used to detect a day rollover in the kiosk's tz. */
function localDateIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function Weather() {
  const api = useApi();
  const qc = useQueryClient();
  const { active } = useActiveClassroom();

  // Scale the card down to fit the live safe box on shorter/Retina displays so
  // it never overflows (never upscales past its natural size).
  const pageRef = useRef<HTMLElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const fitScale = useFitScale(pageRef, cardRef);

  const state = useQuery({
    queryKey: ['weather', active?.id ?? 'none'],
    queryFn: () => api.weather.getState(active!.id),
    enabled: active !== null,
  });

  const today = new Date();
  const month = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

  const history = useQuery({
    queryKey: ['weather-history', active?.id ?? 'none', month],
    queryFn: () => api.weather.getHistory(active!.id, month),
    enabled: active !== null,
  });

  // Optimistic kind: the icon a child just tapped, shown highlighted
  // immediately so the picker never feels dead while the save is in
  // flight. Cleared once the server confirms (or rolled back on error).
  const [pendingKind, setPendingKind] = useState<string | null>(null);
  // Surfaced if a save fails — otherwise a failed PUT looks identical to
  // a working one (nothing happens), which reads as "it won't let me pick".
  const [saveError, setSaveError] = useState<string | null>(null);

  const setToday = useMutation({
    mutationFn: (body: { selectedKind?: string; temperature?: number }) =>
      // Tag the pick with the kiosk's local date so it lands on "today" and the
      // daily reset works in the room's timezone, not the server's UTC.
      api.weather.setToday(active!.id, { ...body, date: localDateIso(new Date()) }),
    onSuccess: (updated) => {
      setSaveError(null);
      // Write the saved value straight into the cache so the picker reflects
      // the pick immediately. getState does a live OpenWeatherMap call, so the
      // background refetch (kept below, to refresh the outdoor temp) can lag —
      // without this the icon would briefly snap back to the previous value.
      qc.setQueryData(['weather', active?.id ?? 'none'], updated);
      qc.invalidateQueries({ queryKey: ['weather', active?.id ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['weather-history', active?.id ?? 'none'] });
    },
    onError: (err) => {
      setPendingKind(null);
      setSaveError(err instanceof Error ? err.message : "Couldn't save — please try again.");
    },
    onSettled: () => setPendingKind(null),
  });

  // Daily reset in the kiosk's LOCAL timezone: only a pick stamped with today's
  // local date shows; anything older (or none) displays fresh — nothing
  // selected, slider clear — when the teacher opens the page the next morning.
  // selected_date is set per pick and is NOT bumped by the outdoor-temp refresh
  // (unlike updatedAt), so this stays reliable. The outdoor temp is never reset.
  const pickIsForToday = state.data?.selectedDate === localDateIso(today);
  const selectedKind = pendingKind ?? (pickIsForToday ? (state.data?.selectedKind ?? null) : null);
  const temperature = pickIsForToday ? (state.data?.temperature ?? null) : null;
  // Server-side fetched outdoor temp (from OpenWeatherMap via the
  // classroom's zip). Shown in a small subdued readout next to the
  // thermometer so kids can match their reading to the real temp.
  const localTemperature = state.data?.localTemperature ?? null;

  // Local draft so a 50→90° drag doesn't fire ~40 PUTs (one per step)
  // before the user releases. Slider always reflects the draft; a
  // debounced effect commits to the API after the user pauses.
  const [draftTemperature, setDraftTemperature] = useState<number | null>(null);
  // Sync the draft from the server when switching classrooms.
  useEffect(() => {
    setDraftTemperature(temperature);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);
  // Initial-load sync: adopt the server value as soon as the query
  // resolves (it can arrive after the classroom-change effect).
  useEffect(() => {
    if (draftTemperature === null && temperature !== null) {
      setDraftTemperature(temperature);
    }
  }, [temperature, draftTemperature]);

  const [historyOpen, setHistoryOpen] = useState(false);

  // Temperature display unit — chosen on the Weather settings page.
  // Re-read on classroom change so different classrooms can use
  // different display formats (e.g. K-2 = words, 5th = numbers).
  const [unit, setUnit] = useState<TemperatureUnit>('F');
  useEffect(() => {
    if (active) setUnit(getTemperatureUnit(active.id));
  }, [active]);

  // Debounced commit. Skip when draft matches the server value (initial
  // sync or just-finished mutation). 200ms idle is short enough that
  // release feels instant but coalesces a fast drag into one PUT.
  useEffect(() => {
    if (draftTemperature === null || draftTemperature === temperature) return;
    const id = setTimeout(() => {
      setToday.mutate({ temperature: draftTemperature });
    }, 200);
    return () => clearTimeout(id);
  }, [draftTemperature, temperature, setToday]);

  if (!active) {
    return (
      <section className={styles.empty} role="status">
        No classroom selected.
      </section>
    );
  }

  return (
    <section className={styles.weather} data-testid="weather-page" ref={pageRef}>
      <div className={styles.card} ref={cardRef} style={{ transform: `scale(${fitScale})` }}>
        <h1 className={styles.title}>What&rsquo;s the weather today?</h1>

        <div className={styles.kindRow} role="radiogroup" aria-label="Weather kind">
          {KINDS.map((k) => (
            <button
              key={k.kind}
              type="button"
              role="radio"
              aria-checked={selectedKind === k.kind}
              aria-pressed={selectedKind === k.kind}
              onClick={() => {
                setPendingKind(k.kind);
                setToday.mutate({ selectedKind: k.kind });
              }}
              className={styles.kindButton}
              data-testid={`weather-kind-${k.kind}`}
            >
              <img src={k.img} alt="" className={styles.kindIcon} />
              <span>{k.label}</span>
            </button>
          ))}
        </div>

        {saveError ? (
          <p className={styles.saveError} role="alert" data-testid="weather-save-error">
            Couldn&rsquo;t save the weather: {saveError}
          </p>
        ) : null}

        <div className={styles.thermometerRow}>
          <div className={styles.thermometerWrap}>
            <Thermometer value={draftTemperature} onChange={setDraftTemperature} unit={unit} />
          </div>
        </div>

        <div className={styles.actionsRow}>
          {localTemperature !== null ? (
            <aside className={styles.localTemp} data-testid="local-temperature">
              <span className={styles.localTempLabel}>Right now</span>
              <span className={styles.localTempValue}>
                {/* When the classroom is in "descriptive words" mode the
                 *  outdoor reading still shows in °F so kids learn to
                 *  associate the real number with the word they're
                 *  picking on the gauge. Celsius still flips to °C. */}
                {formatTemperature(localTemperature, unit === 'words' ? 'F' : unit)}
              </span>
              <span className={styles.localTempHint}>outside</span>
            </aside>
          ) : (
            <span aria-hidden="true" />
          )}
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            className={styles.historyButton}
            data-testid="weather-history-open"
          >
            Weather history
          </button>
        </div>
      </div>

      {historyOpen ? (
        <WeatherHistoryModal
          history={history.data ?? []}
          unit={unit}
          onClose={() => setHistoryOpen(false)}
        />
      ) : null}
    </section>
  );
}
